import type { Email, MailFolder } from "../../services/graphMailService";
import type { MailLinkBatchItemInput } from "../../services/mailLinkService";
import { collectInboxFolderIds } from "./mailSearch";

/**
 * Welche Nachrichten eines Verlaufs wandern beim Zuweisen an den Vorgang?
 *
 * Bis 09/2026 hängte „Einem Vorgang zuweisen…" ausschließlich die GEÖFFNETE
 * Nachricht an den Vorgang. Der Rest des Verlaufs blieb draußen, obwohl die
 * Zuweisung fachlich dem ganzen Schriftwechsel gilt — wer im Vorgang nachlas,
 * fand eine Antwort ohne die Frage.
 *
 * Die Zukunft war dabei längst abgedeckt: Neue eingehende Antworten erbt der
 * Delta-Poll über die `conversationId` (`mail-auto-link.service`), gesendete
 * erbt der Sendepfad (`resolveAutoLinkTargets`). Gefehlt hat allein die
 * RÜCKWIRKUNG auf die bereits vorhandenen Nachrichten — genau die stellt diese
 * Datei zusammen.
 *
 * Zwei Regeln tragen das Ergebnis:
 *
 *  1. **Die geöffnete Mail ist immer dabei** — auch wenn der Verlauf gar nicht
 *     geladen werden konnte. Die Zuweisung, die der Nutzer ausgelöst hat, darf
 *     niemals an einem fehlgeschlagenen Nebenweg scheitern.
 *  2. **Die geöffnete Mail trägt nie `isInInbox`.** Sie wird nach dem Verlinken
 *     auf dem angestammten Weg archiviert (`finishRead` schließt das Fenster
 *     und frischt die Listen auf); täte der Batch das zusätzlich, liefe der
 *     zweite Zugriff auf eine bereits verschobene — und damit unbekannte — Id.
 */

/**
 * Obergrenze je Zuweisung. Jede Nachricht kostet serverseitig einen
 * Lese-Zugriff und ggf. einen Verschiebe-Aufruf gegen Graph; ein entarteter
 * Thread (Newsletter-Konversationen erreichen dreistellige Längen) ließe den
 * Klick sonst minutenlang hängen. Was nicht mehr hineinpasst, wird gemeldet
 * statt still weggelassen.
 */
export const VERLAUF_MAX_NACHRICHTEN = 50;

export type VerlaufPlan = {
    /** Die Nachrichten für `POST /mail/links/batch`, Thread-Reihenfolge. */
    items: MailLinkBatchItemInput[];
    /** Wie viele Verlaufs-Nachrichten die Obergrenze ausgelassen hat. */
    ausgelassen: number;
};

/** „An"-Empfänger als Anzeige-String (T6-Snapshot). */
function empfaenger(email: Email): string | null {
    const namen = (email.to ?? []).map((a) => a.name || a.address).filter(Boolean);
    return namen.length ? namen.join(", ") : null;
}

function iso(d: unknown): string | null {
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
}

/**
 * Baut den Batch aus der geöffneten Mail und ihrem Verlauf.
 *
 * `offen` ist der bereits vorhandene Snapshot der geöffneten Nachricht (die
 * einzige Quelle, die auch ohne geladenen Verlauf verlässlich vollständig ist).
 * `verlauf` sind die Nachrichten derselben Konversation — leer, wenn es keine
 * gibt oder das Laden fehlgeschlagen ist.
 *
 * `imPosteingang` entscheidet je Nachricht, ob der Server sie archivieren darf.
 * Wo der Ordnerbaum das nicht hergibt, lautet die Antwort konservativ `false`:
 * eine nicht archivierte Mail ist ein Schönheitsfehler, eine fälschlich
 * verschobene ein Datenproblem.
 */
export function baueVerlaufBatch(
    mailbox: string,
    offen: MailLinkBatchItemInput,
    verlauf: readonly Email[],
    imPosteingang: (email: Email) => boolean,
    max: number = VERLAUF_MAX_NACHRICHTEN,
): VerlaufPlan {
    const offeneId = offen.messageId;
    const weitere: MailLinkBatchItemInput[] = [];
    const gesehen = new Set<string>([offeneId]);
    // Zweite Achse neben der Graph-`id`: DIESELBE MAIL. Der Verlauf kommt
    // postfachweit über alle Ordner, damit die eigenen Antworten aus
    // „Gesendet" mitkommen — eine selbst gesendete Mail liegt darin deshalb
    // zweimal (Sendekopie + zugestellte Kopie), mit verschiedener `id`, aber
    // gleicher `internetMessageId`. Ohne diese Prüfung landete sie zweimal im
    // Vorgang. Von zwei Kopien gewinnt die im POSTEINGANG: nur sie darf der
    // Batch archivieren — behielte man die Sendekopie (sie steht im Verlauf
    // zuerst, weil früher gesendet), bliebe die zugestellte unarchiviert liegen.
    //
    // OFFEN = -1: die geöffnete Mail steht als `items[0]` fest und wird hier nie
    // ersetzt — sie archiviert ihr eigener Weg (`finishRead`), ein zweiter Move
    // aus dem Batch käme dem in die Quere.
    const OFFEN = -1;
    const posNachMail = new Map<string, number>();
    const offeneImid = (offen.internetMessageId ?? "").trim();
    if (offeneImid) posNachMail.set(offeneImid, OFFEN);

    const alsItem = (m: Email, imid: string): MailLinkBatchItemInput => ({
        mailbox,
        messageId: m.id,
        conversationId: m.conversationId || offen.conversationId || null,
        internetMessageId: imid || null,
        subject: m.subject || null,
        senderName: m.senderName || null,
        senderEmail: m.senderEmail || null,
        recipients: empfaenger(m),
        receivedAt: iso(m.date),
        preview: m.preview || null,
        isInInbox: imPosteingang(m),
    });

    for (const m of verlauf) {
        if (!m?.id || gesehen.has(m.id)) continue;
        gesehen.add(m.id);
        const imid = (m.internetMessageId ?? "").trim();
        const pos = imid ? posNachMail.get(imid) : undefined;

        if (pos !== undefined) {
            // Diese Mail steht schon im Plan. Eine zweite Kopie lohnt nur, wenn
            // SIE im Posteingang liegt — sonst bliebe sie dort unarchiviert.
            if (!imPosteingang(m)) continue;
            if (pos !== OFFEN) {
                if (weitere[pos].isInInbox) continue;
                weitere[pos] = alsItem(m, imid);
                continue;
            }
            // Dublette der GEÖFFNETEN Mail: sie kommt zusätzlich mit, damit der
            // Batch sie verschiebt. Verknüpft wird trotzdem nur einmal — der
            // Server entdoppelt über dieselbe `internetMessageId`.
        }

        if (imid) posNachMail.set(imid, weitere.length);
        weitere.push(alsItem(m, imid));
    }

    // Die geöffnete Mail zählt gegen die Obergrenze mit; bei Überlänge weichen
    // die ÄLTESTEN Nachrichten — der jüngere Teil des Verlaufs ist der, den man
    // im Vorgang sucht.
    const platz = Math.max(0, max - 1);
    const ausgelassen = Math.max(0, weitere.length - platz);
    const behalten = ausgelassen > 0 ? weitere.slice(ausgelassen) : weitere;

    return {
        items: [{ ...offen, mailbox, isInInbox: false }, ...behalten],
        ausgelassen,
    };
}

/**
 * Meldung nach dem Zuweisen — `null`, wenn alles glatt lief und nichts zu
 * sagen ist. Teilfehler werden benannt: Eine Zuweisung, die zur Hälfte
 * gescheitert ist, darf sich nicht wie ein Erfolg anfühlen.
 */
export function verlaufMeldung(
    ergebnisse: readonly { error?: string; archiveError?: boolean }[],
    ausgelassen: number,
): string | null {
    const teile: string[] = [];
    const fehler = ergebnisse.filter((r) => r.error).length;
    if (fehler > 0) {
        teile.push(`${fehler} von ${ergebnisse.length} Nachrichten des Verlaufs konnten nicht zugeordnet werden.`);
    }
    const archivFehler = ergebnisse.filter((r) => !r.error && r.archiveError).length;
    if (archivFehler > 0) {
        teile.push(
            archivFehler === 1
                ? "Eine Nachricht wurde zugeordnet, aber nicht archiviert (bereits verschoben?)."
                : `${archivFehler} Nachrichten wurden zugeordnet, aber nicht archiviert (bereits verschoben?).`,
        );
    }
    if (ausgelassen > 0) {
        teile.push(
            `Der Verlauf ist länger als ${VERLAUF_MAX_NACHRICHTEN} Nachrichten — die ${ausgelassen} ältesten wurden nicht zugeordnet.`,
        );
    }
    return teile.length ? teile.join(" ") : null;
}

/**
 * Die Posteingangs-Prüfung für ein Postfach, gebaut aus dem geladenen
 * Ordnerbaum des MailContext.
 *
 * Ist der Baum (noch) nicht da, sagt sie zu allem `false` — die Nachricht wird
 * dann verlinkt, aber nicht verschoben. Das ist die richtige Richtung zu irren:
 * eine liegengebliebene Mail sieht der Nutzer, eine fälschlich verschobene
 * sucht er.
 */
export function posteingangsPruefung(
    folderTrees: Readonly<Record<string, MailFolder[]>>,
    mailbox: string,
): (email: Email) => boolean {
    const inboxIds = collectInboxFolderIds(folderTrees[mailbox] ?? []);
    return (email: Email) => {
        const pid = email?.parentFolderId;
        return !!pid && inboxIds.has(pid);
    };
}

/**
 * T-29 — in der Einzelansicht sieht man die übrigen Mails einer Unterhaltung
 * als eigene Zeilen. Wandern sie beim Zuordnen mit (ganzer Schriftwechsel,
 * #1599), verschwänden sie sonst kommentarlos aus dem Posteingang. Gezählt
 * wird nur, was der Batch tatsächlich archiviert hat; die geöffnete Mail
 * selbst nicht, die ist ja gemeint.
 */
export function mitgenommeneMeldung(
    ergebnisse: readonly { messageId: string; archived?: boolean; error?: string }[],
    offenMessageId: string,
): string | null {
    const n = ergebnisse.filter((r) => !r.error && r.archived && r.messageId !== offenMessageId).length;
    if (n === 0) return null;
    return n === 1
        ? "Eine weitere Mail aus derselben Unterhaltung wurde mit zugeordnet und aus dem Posteingang archiviert."
        : `${n} weitere Mails aus derselben Unterhaltung wurden mit zugeordnet und aus dem Posteingang archiviert.`;
}
